import { useCallback, useRef, useEffect } from "react";

interface SwipeDetectionOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeStart?: () => void;
  onSwipeEnd?: () => void;
  onSwipeMove?: (deltaX: number, progress: number) => void;
  minSwipeDistance?: number;
  maxSwipeTime?: number;
  enableSwipe?: boolean;
}

interface TouchPosition {
  x: number;
  y: number;
  timestamp: number;
}

export const useSwipeDetection = <T extends HTMLElement = HTMLDivElement>({
  onSwipeLeft,
  onSwipeRight,
  onSwipeStart,
  onSwipeEnd,
  onSwipeMove,
  minSwipeDistance = 75,
  maxSwipeTime = 300,
  enableSwipe = true,
}: SwipeDetectionOptions) => {
  const touchStartRef = useRef<TouchPosition | null>(null);
  const touchEndRef = useRef<TouchPosition | null>(null);
  const elementRef = useRef<T | null>(null);
  const gestureLockRef = useRef<"horizontal" | "vertical" | null>(null);
  const swipeActiveRef = useRef(false);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enableSwipe) return;
      if ((e.target as HTMLElement)?.closest?.("[data-no-swipe]")) return;

      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        timestamp: Date.now(),
      };
      touchEndRef.current = null;
      gestureLockRef.current = null;
      swipeActiveRef.current = false;
    },
    [enableSwipe]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!enableSwipe || !touchStartRef.current) return;

      const touch = e.touches[0];
      touchEndRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        timestamp: Date.now(),
      };

      // Calculate real-time movement
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const horizontalDistance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);

      if (!gestureLockRef.current) {
        if (horizontalDistance < 12 && verticalDistance < 12) {
          return;
        }

        if (verticalDistance >= horizontalDistance) {
          gestureLockRef.current = "vertical";
          return;
        }

        gestureLockRef.current = "horizontal";
        swipeActiveRef.current = true;
        onSwipeStart?.();
      }

      if (gestureLockRef.current === "horizontal") {
        const containerWidth = 300; // Approximate container width for progress calculation
        const progress = Math.min(horizontalDistance / containerWidth, 1);
        onSwipeMove?.(deltaX, progress);
      }
    },
    [enableSwipe, onSwipeMove, onSwipeStart]
  );

  const handleTouchEnd = useCallback(() => {
    if (!enableSwipe || !touchStartRef.current || !touchEndRef.current) {
      touchStartRef.current = null;
      touchEndRef.current = null;
      gestureLockRef.current = null;
      if (swipeActiveRef.current) {
        swipeActiveRef.current = false;
        onSwipeEnd?.();
      }
      return;
    }

    const timeDiff =
      touchEndRef.current.timestamp - touchStartRef.current.timestamp;
    const horizontalDistance = touchEndRef.current.x - touchStartRef.current.x;
    const verticalDistance = Math.abs(
      touchEndRef.current.y - touchStartRef.current.y
    );

    // Reset touch positions
    touchStartRef.current = null;
    touchEndRef.current = null;
    const wasHorizontalSwipe = gestureLockRef.current === "horizontal";
    gestureLockRef.current = null;

    // Check if it's a valid swipe
    if (
      wasHorizontalSwipe &&
      timeDiff <= maxSwipeTime &&
      Math.abs(horizontalDistance) >= minSwipeDistance &&
      verticalDistance < 100 // Prevent accidental vertical swipes
    ) {
      if (horizontalDistance > 0) {
        // Swipe right
        onSwipeRight?.();
      } else {
        // Swipe left
        onSwipeLeft?.();
      }
    }

    if (swipeActiveRef.current) {
      swipeActiveRef.current = false;
      onSwipeEnd?.();
    }
  }, [
    enableSwipe,
    onSwipeLeft,
    onSwipeRight,
    onSwipeEnd,
    minSwipeDistance,
    maxSwipeTime,
  ]);

  // Attach event listeners
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enableSwipe) return;

    // Add passive event listeners for better performance
    element.addEventListener("touchstart", handleTouchStart, { passive: true });
    element.addEventListener("touchmove", handleTouchMove, { passive: true });
    element.addEventListener("touchend", handleTouchEnd, { passive: true });
    element.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
      element.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, enableSwipe]);

  return elementRef;
};
